import { VectorTile } from '@mapbox/vector-tile'
import Pbf from 'pbf'
import { cotrexLayerService } from '../cotrex/cotrex-layer-service.js'
import { bboxToTileRange } from '../../lib/tile-math.js'
import { logger } from '../../utils/logger.js'
import { OFM_PINNED_TILE_URL, OFM_TILEJSON_URL } from '../../lib/ofm-config.js'

const log = logger.create('trail-extractor')

const TRAIL_ZOOM = 14

let ofmTileUrlTemplate: string | null = null

async function getOsmTileUrl(): Promise<string | null> {
  if (OFM_PINNED_TILE_URL) return OFM_PINNED_TILE_URL
  if (ofmTileUrlTemplate) return ofmTileUrlTemplate

  try {
    const resp = await fetch(OFM_TILEJSON_URL)
    if (!resp.ok) return null
    const json = await resp.json()
    if (json.tiles && json.tiles.length > 0) {
      ofmTileUrlTemplate = json.tiles[0]
      return ofmTileUrlTemplate
    }
  } catch (err) {
    log.warn('Failed to fetch OpenFreeMap TileJSON:', err)
  }
  return null
}

export interface TrailSegment {
  coordinates: [number, number][] // [lng, lat] pairs
}

async function extractCotrexTrails(
  bbox: [number, number, number, number]
): Promise<TrailSegment[]> {
  const pmtiles = cotrexLayerService.getPMTilesInstance()
  if (!pmtiles) {
    log.warn('COTREX PMTiles not available')
    return []
  }

  const { minX, minY, maxX, maxY } = bboxToTileRange(bbox, TRAIL_ZOOM)
  const segments: TrailSegment[] = []

  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      try {
        const resp = await pmtiles.getZxy(TRAIL_ZOOM, x, y)
        if (!resp) continue

        const tile = new VectorTile(new Pbf(resp.data))
        const layer = (tile.layers as Partial<typeof tile.layers>)['cotrex_trails']
        if (!layer) continue

        for (let i = 0; i < layer.length; i++) {
          const feat = layer.feature(i)
          const geojson = feat.toGeoJSON(x, y, TRAIL_ZOOM)

          if (geojson.geometry.type === 'LineString') {
            segments.push({
              coordinates: geojson.geometry.coordinates as [number, number][],
            })
          } else if (geojson.geometry.type === 'MultiLineString') {
            for (const line of geojson.geometry.coordinates) {
              segments.push({ coordinates: line as [number, number][] })
            }
          }
        }
      } catch (err) {
        log.warn(`Failed to read COTREX tile z${TRAIL_ZOOM}/${x}/${y}:`, err)
      }
    }
  }

  log.info(`Extracted ${segments.length} COTREX trail segments`)
  return segments
}

async function extractOsmTrails(bbox: [number, number, number, number]): Promise<TrailSegment[]> {
  const urlTemplate = await getOsmTileUrl()
  if (!urlTemplate) {
    log.warn('OSM tile URL not available')
    return []
  }

  const { minX, minY, maxX, maxY } = bboxToTileRange(bbox, TRAIL_ZOOM)
  const segments: TrailSegment[] = []

  const fetches: Promise<void>[] = []

  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      const url = urlTemplate
        .replace('{z}', String(TRAIL_ZOOM))
        .replace('{x}', String(x))
        .replace('{y}', String(y))

      fetches.push(
        fetch(url)
          .then(resp => {
            if (!resp.ok) return
            return resp.arrayBuffer()
          })
          .then(buf => {
            if (!buf) return
            const tile = new VectorTile(new Pbf(buf))
            const layer = (tile.layers as Partial<typeof tile.layers>)['transportation']
            if (!layer) return

            for (let i = 0; i < layer.length; i++) {
              const feat = layer.feature(i)
              const cls = feat.properties.class
              if (cls !== 'path' && cls !== 'track') continue

              const geojson = feat.toGeoJSON(x, y, TRAIL_ZOOM)

              if (geojson.geometry.type === 'LineString') {
                segments.push({
                  coordinates: geojson.geometry.coordinates as [number, number][],
                })
              } else if (geojson.geometry.type === 'MultiLineString') {
                for (const line of geojson.geometry.coordinates) {
                  segments.push({ coordinates: line as [number, number][] })
                }
              }
            }
          })
          .catch(err => {
            log.warn(`Failed to fetch OSM tile z${TRAIL_ZOOM}/${x}/${y}:`, err)
          })
      )
    }
  }

  await Promise.all(fetches)
  log.info(`Extracted ${segments.length} OSM trail segments`)
  return segments
}

export async function extractTrailsInBbox(
  bbox: [number, number, number, number]
): Promise<TrailSegment[]> {
  const [cotrex, osm] = await Promise.all([extractCotrexTrails(bbox), extractOsmTrails(bbox)])

  const combined = [...cotrex, ...osm]
  log.info(
    `Total trail segments: ${combined.length} (COTREX: ${cotrex.length}, OSM: ${osm.length})`
  )
  return combined
}
